import _ from 'lodash';

import { AutoFocusType } from 'components/booking-modal/BookingProfileFormTab';
import { Instant } from 'js-joda';
import { NoteTypeAndScope, dashboard } from 'types';
import { PATRON_NOTE_SCOPE_TO_TYPE_TO_TITLE } from 'constants/PatronNoteTypes';
import { getBusinessGuestProfile } from 'utils/guestProfileUtils';
import { toEnumString } from '@tock/coretypes';
import BookingNoteTypes from 'constants/BookingNoteTypes';
import protos from 'utils/protos';

const { BookingNote, NoteType } = protos;

export function getPatronNoteTitle(
  noteType: dashboard.NoteType,
  noteScope: dashboard.NoteScope
): string {
  const title = PATRON_NOTE_SCOPE_TO_TYPE_TO_TITLE[noteScope]?.[noteType] ?? 'Notes';
  return _.capitalize(title[0]) + _.toLower(title).substring(1);
}

/**
 * takes a note and active scopes and give you the matching scope
 */
export function getScopeForType(
  noteTypeAsString: string, // See the comment in core/notes.proto about why this is a string rather than an enum
  activeScopes: NoteTypeAndScope[]
): dashboard.NoteScope {
  const scopeForType = _.find(
    activeScopes,
    ({ type: scopeType }) => noteTypeAsString === scopeType
  );
  return scopeForType ? scopeForType.scope : 'BUSINESS';
}

export function findNoteWithNoteType(
  noteType: string,
  notesWithNoteType?: dashboard.AuditedNote[] | dashboard.IAuditedNote[]
): dashboard.AuditedNote | dashboard.IAuditedNote | undefined {
  return _.find(notesWithNoteType, (note: dashboard.IAuditedNote) => {
    // Default to noteType GENERAL if it's missing, since that means we're on the older server
    // TODO: Remove fallback
    const defaultNoteType = toEnumString(
      BookingNote.BookingNoteType,
      BookingNote.BookingNoteType.GENERAL
    );
    return (note.noteType || defaultNoteType) === noteType;
  });
}

export function filterAndSortNotesByType(
  auditedNotes: dashboard.AuditedNote[] | dashboard.IAuditedNote[]
): dashboard.AuditedNote[] {
  const notesWithContent = _.filter(auditedNotes, (note) => {
    return !_.isEmpty(note.note);
  });
  // Sort notes here is by string. Better to use an actual, useful sort order
  // but given that there are only two options, this seems fine right now.
  return _.orderBy(notesWithContent, ['noteType'], ['desc']) as dashboard.AuditedNote[];
}

export function replacePatronNoteInProfile(
  guestProfile: dashboard.GuestProfile,
  newNote: dashboard.PatronNote,
  noteType: dashboard.NoteType,
  businessId: number
): void {
  const businessGuestProfile = getBusinessGuestProfile(guestProfile, businessId, newNote.noteScope);
  if (!businessGuestProfile) {
    return;
  }

  const notes = businessGuestProfile.note ?? [];
  const note = notes.find(
    (note) => toEnumString(NoteType, note.noteType) === toEnumString(NoteType, noteType)
  );
  if (note) {
    note.lastUpdatedAtTimestampMs = Instant.now().toEpochMilli();
    note.lastUpdatedBy = newNote.createdBy;
    note.note = newNote.note;
    note.previousVersionId = newNote.id;
  } else {
    const noteDetails = {
      lastUpdatedAtTimestampMs: Instant.now().toEpochMilli(),
      lastUpdatedBy: newNote.createdBy,
      note: newNote.note,
      noteType: toEnumString(NoteType, noteType),
      previousVersionId: newNote.id,
    };

    if (_.isArray(businessGuestProfile.note)) {
      businessGuestProfile.note.push(noteDetails);
    } else {
      businessGuestProfile.note = [noteDetails];
    }
  }
}

export function bookingNoteTypeToAutoFocusValue(noteType: BookingNoteTypes): AutoFocusType {
  switch (noteType) {
    case BookingNoteTypes.GENERAL: {
      return 'VISIT_NOTE';
    }
    case BookingNoteTypes.DIETARY: {
      return 'VISIT_DIETARY_NOTE';
    }
    case BookingNoteTypes.GUEST_PROVIDED: {
      return 'GUEST_PROVIDED_ORDER_NOTE';
    }
  }
}

export function patronNoteTypeToAutoFocusValue(noteType: BookingNoteTypes): AutoFocusType {
  switch (noteType) {
    case BookingNoteTypes.GENERAL: {
      return 'GUEST_NOTE';
    }
    case BookingNoteTypes.DIETARY: {
      return 'GUEST_DIETARY_NOTE';
    }
    default: {
      return 'GUEST_NOTE';
    }
  }
}
